// =============================================================================
// 관리 이력(Management history) 분류 체계 — audit_log의 raw action(domain.verb)을
// 사람이 읽는 메뉴/구분/내용으로 파생한다. audit_log 스키마에는 이 분류가 없어
// (action 문자열만 있음) 여기서 매핑한다. 백엔드(파생·필터·CSV)와 FE(필터 옵션)가
// 공유한다. 매핑 규칙은 데이터 주도라 추가/조정이 쉽다.
//
// Figma '관리 이력 Case' 표 기준:
//   메뉴(1Depth): 미디어/스케줄/디바이스/관리 이력(전 관리자) + BRICK 관리(BO) +
//   H9(BO·에이치나인). 내용은 케이스별 템플릿으로 metadata의 이름을 치환한다.
// =============================================================================

/** 화면 메뉴(필터·컬럼). 순서 = 드롭다운 노출 순서. BRICK 관리·H9는 BO 전용. */
export const AUDIT_MENUS = [
  '미디어 관리',
  '스케줄 관리',
  '디바이스 관리',
  '관리 이력',
  'BRICK 관리',
  'H9',
  '기타',
] as const;
export type AuditMenu = (typeof AUDIT_MENUS)[number];

/** 작업 구분(필터·컬럼). Figma Case 표의 구분 값 전체. */
export const AUDIT_CATEGORIES = [
  '로그인',
  '추가',
  '수정',
  '삭제',
  '변경',
  '갱신',
  '연동',
  '해제',
  '활성화',
  '비활성화',
  '기타',
] as const;
export type AuditCategory = (typeof AUDIT_CATEGORIES)[number];

type Meta = Record<string, unknown> | null | undefined;

function m(meta: Meta, key: string): string {
  const v = meta?.[key];
  return typeof v === 'string' && v.trim() ? v : '-';
}

/** 케이스 정의 — action별 메뉴/구분/내용 템플릿 (Figma '관리 이력 Case'). */
const CASES: Record<
  string,
  { menu: AuditMenu; category: AuditCategory; content: (meta: Meta) => string }
> = {
  // ── 미디어 관리 ──
  'playlist.create': { menu: '미디어 관리', category: '추가', content: (x) => `신규 플레이리스트 [${m(x, 'name')}] 추가` },
  'playlist.delete': { menu: '미디어 관리', category: '삭제', content: (x) => `플레이리스트 [${m(x, 'name')}] 삭제` },
  'playlist.media_update': { menu: '미디어 관리', category: '수정', content: (x) => `플레이리스트 [${m(x, 'name')}] 미디어 수정` },
  'playlist.rename': { menu: '미디어 관리', category: '변경', content: (x) => `[${m(x, 'oldName')}] 플레이리스트명 변경 : [${m(x, 'newName')}]` },
  'media.upload': { menu: '미디어 관리', category: '추가', content: (x) => `신규 미디어 [${m(x, 'name')}] 업로드` },
  'media.delete': { menu: '미디어 관리', category: '삭제', content: (x) => `미디어 [${m(x, 'name')}] 삭제` },
  'notice.create': { menu: '미디어 관리', category: '추가', content: (x) => x?.['template'] === true ? `슬라이드 [${m(x, 'name')}] 템플릿으로 저장` : `신규 슬라이드 [${m(x, 'name')}] 추가` },
  'notice.delete': { menu: '미디어 관리', category: '삭제', content: (x) => x?.['template'] === true ? `템플릿 [${m(x, 'name')}] 삭제` : `슬라이드 [${m(x, 'name')}] 삭제` },
  'notice.update': { menu: '미디어 관리', category: '수정', content: (x) => `슬라이드 [${m(x, 'name')}] 수정` },
  // ── 미디어 관리 · 폴더(2026-09-16) — 삭제는 안의 항목이 함께 지워질 수 있어 이력에 꼭 남긴다 ──
  'folder.create': { menu: '미디어 관리', category: '추가', content: (x) => `신규 폴더 [${m(x, 'name')}] 추가` },
  'folder.rename': { menu: '미디어 관리', category: '변경', content: (x) => `[${m(x, 'oldName')}] 폴더명 변경 : [${m(x, 'newName')}]` },
  'folder.delete': { menu: '미디어 관리', category: '삭제', content: (x) => {
    const count = typeof x?.['count'] === 'number' ? x['count'] as number : null;
    const kept = x?.['contents'] === 'keep';
    const tail = count === null || count === 0 ? '' : kept ? ` — 항목 ${count}개는 지정되지 않음으로 이동` : ` — 항목 ${count}개 함께 삭제`;
    return `폴더 [${m(x, 'name')}] 삭제${tail}`;
  } },
  // ── 스케줄 관리 ──
  'schedule_type.create': { menu: '스케줄 관리', category: '추가', content: (x) => `신규 타임라인 [${m(x, 'name')}] 추가` },
  'schedule_type.schedule_update': { menu: '스케줄 관리', category: '수정', content: (x) => `타임라인 [${m(x, 'name')}] 스케줄 수정` },
  'schedule_type.rename': { menu: '스케줄 관리', category: '변경', content: (x) => `[${m(x, 'oldName')}] 타임라인명 변경 : [${m(x, 'newName')}]` },
  'schedule_type.delete': { menu: '스케줄 관리', category: '삭제', content: (x) => `타임라인 [${m(x, 'name')}] 삭제` },
  // 편성 캘린더에서 만든 스케줄. 예전 타임라인과 같은 메뉴에 쌓인다.
  'schedule.assignment.create': { menu: '스케줄 관리', category: '추가', content: (x) => `신규 스케줄 [${m(x, 'name')}] 추가` },
  'schedule.assignment.update': { menu: '스케줄 관리', category: '수정', content: (x) => `스케줄 [${m(x, 'name')}] 수정` },
  'schedule.assignment.delete': { menu: '스케줄 관리', category: '삭제', content: (x) => `스케줄 [${m(x, 'name')}] 삭제` },
  'schedule.assignment.reorder': { menu: '스케줄 관리', category: '수정', content: (x) => `스케줄 [${m(x, 'name')}] 재생 순서 변경` },
  'schedule.assignment.approve': { menu: '스케줄 관리', category: '기타', content: (x) => `스케줄 [${m(x, 'name')}] 승인` },
  'schedule.assignment.reject': { menu: '스케줄 관리', category: '기타', content: (x) => `스케줄 [${m(x, 'name')}] 반려` },
  'schedule.assignment.cancel': { menu: '스케줄 관리', category: '기타', content: (x) => `스케줄 [${m(x, 'name')}] 승인 요청 취소` },
  // ── 디바이스 관리 ──
  'device.pair': { menu: '디바이스 관리', category: '추가', content: (x) => `신규 디바이스 [${m(x, 'name')}] 페어링` },
  'device.update': { menu: '디바이스 관리', category: '수정', content: (x) => `[${m(x, 'name')}] 수정` },
  'device.delete': { menu: '디바이스 관리', category: '삭제', content: (x) => `[${m(x, 'name')}] 삭제` },
  'device.renew': { menu: '디바이스 관리', category: '갱신', content: (x) => `[${m(x, 'name')}] 페어링 갱신` },
  'device.renew_code': { menu: '디바이스 관리', category: '갱신', content: (x) => `[${m(x, 'name')}] 페어링 갱신 코드 발급` },
  'device.rename': { menu: '디바이스 관리', category: '변경', content: (x) => `[${m(x, 'oldName')}] 디바이스명 변경 : [${m(x, 'newName')}]` },
  // 아래 둘은 CASES 에 없어 '기타'로 떨어지고 있었다 — 관리 이력 필터가 이를
  // 디바이스 관리로 잡지 못했다.
  'device.command': { menu: '디바이스 관리', category: '기타', content: (x) => `디바이스 명령 실행 : [${m(x, 'command')}]` },
  'device.reissue': { menu: '디바이스 관리', category: '갱신', content: () => '디바이스 토큰 재발급' },
  // ── 관리 이력 ──
  'history.export': { menu: '관리 이력', category: '기타', content: (x) => `[${m(x, 'actorName')}] 관리 이력 다운로드` },
  // ── BRICK 관리 · 조직 관리 ──
  // 기존 액션 별칭 — tenant.*(BO 조직 CRUD)·member.*(조직 내 관리자)는 이미
  // 기록 중이라 액션명을 바꾸지 않고 Case 문구만 입힌다.
  'tenant.create': { menu: 'BRICK 관리', category: '추가', content: (x) => `신규 조직 [${m(x, 'name')}] 추가` },
  'member.invite': { menu: 'BRICK 관리', category: '추가', content: (x) => `관리자 [${m(x, 'email')}] 추가` },
  'member.role_change': { menu: 'BRICK 관리', category: '수정', content: (x) => `관리자 [${m(x, 'email')}] 수정` },
  'member.remove': { menu: 'BRICK 관리', category: '삭제', content: (x) => `관리자 [${m(x, 'email')}] 삭제` },
  'org.create': { menu: 'BRICK 관리', category: '추가', content: (x) => `신규 조직 [${m(x, 'name')}] 추가` },
  'org.update': { menu: 'BRICK 관리', category: '수정', content: (x) => `[${m(x, 'name')}] 수정` },
  'org.delete': { menu: 'BRICK 관리', category: '삭제', content: (x) => `[${m(x, 'name')}] 삭제` },
  'org.admin_add': { menu: 'BRICK 관리', category: '추가', content: (x) => `[${m(x, 'orgName')}] 내 관리자 [${m(x, 'adminName')}] 추가` },
  'org.admin_update': { menu: 'BRICK 관리', category: '수정', content: (x) => `[${m(x, 'orgName')}] 내 관리자 [${m(x, 'adminName')}] 수정` },
  'org.admin_password': { menu: 'BRICK 관리', category: '변경', content: (x) => `[${m(x, 'orgName')}] 내 관리자 [${m(x, 'adminName')}] 비밀번호 변경` },
  'org.admin_delete': { menu: 'BRICK 관리', category: '삭제', content: (x) => `[${m(x, 'orgName')}] 내 관리자 [${m(x, 'adminName')}] 삭제` },
  // ── BRICK 관리 · 고객사로 보기(BO 대행, 2026-09-15 A안) ──
  'bo.impersonation_start': { menu: 'BRICK 관리', category: '기타', content: (x) =>
    `운영사(H9) 고객사 화면 보기 시작 — ${x?.['scope'] === 'write' ? '편집 허용' : '읽기 전용'}${typeof x?.['reason'] === 'string' && x['reason'] ? ` · 사유: ${x['reason']}` : ''}` },
  'bo.impersonation_end': { menu: 'BRICK 관리', category: '기타', content: (x) =>
    x?.['replacedBy'] ? '운영사(H9) 고객사 화면 보기 — 권한 전환(이전 세션 종료)' : '운영사(H9) 고객사 화면 보기 종료' },
  // ── H9 · 공지사항 관리 ──
  'h9.notice_enable': { menu: 'H9', category: '활성화', content: (x) => `[${m(x, 'space')}] ${m(x, 'title')} 활성화` },
  'h9.notice_disable': { menu: 'H9', category: '비활성화', content: (x) => `[${m(x, 'space')}] ${m(x, 'title')} 비활성화` },
  'h9.notice_delete': { menu: 'H9', category: '삭제', content: (x) => `[${m(x, 'space')}] ${m(x, 'title')} 삭제` },
  // ── H9 · 앱 연동 ──
  'h9.app_enable': { menu: 'H9', category: '연동', content: (x) => `[${m(x, 'app')}] 연동 활성화` },
  'h9.app_disable': { menu: 'H9', category: '해제', content: (x) => `[${m(x, 'app')}] 연동 비활성화` },
  'h9.app_account_set': { menu: 'H9', category: '연동', content: (x) => `[${m(x, 'app')}] ${m(x, 'account')} 계정 연결` },
  'h9.app_account_change': { menu: 'H9', category: '변경', content: (x) => `[${m(x, 'app')}] ${m(x, 'oldAccount')} 계정 변경 : ${m(x, 'newAccount')}` },
  'h9.chat_space_update': { menu: 'H9', category: '수정', content: (x) => `[${m(x, 'app')}] 수신 스페이스 수정` },
  'h9.chat_approval_space_update': { menu: 'H9', category: '수정', content: (x) => `[${m(x, 'app')}] 승인 스페이스 수정` },
};

/** raw action → 메뉴. 케이스 우선, 없으면 domain(첫 토큰) 기준 폴백. */
export function auditMenu(action: string): AuditMenu {
  const c = CASES[action];
  if (c) return c.menu;
  const domain = action.split('.')[0] ?? '';
  if (domain === 'bo' || ['member', 'grant', 'org', 'tenant', 'user'].includes(domain))
    return 'BRICK 관리';
  if (domain === 'h9') return 'H9';
  if (domain === 'history') return '관리 이력';
  if (domain === 'device') return '디바이스 관리';
  if (['schedule', 'event', 'timeline-block', 'room', 'schedule_type'].includes(domain)) return '스케줄 관리';
  if (['playlist', 'media', 'scene', 'notice', 'play'].includes(domain)) return '미디어 관리';
  return '기타';
}

/** raw action → 구분. 케이스 우선, 없으면 verb 기준 폴백. */
export function auditCategory(action: string): AuditCategory {
  const c = CASES[action];
  if (c) return c.category;
  if (action === 'auth.login' || action.endsWith('.login')) return '로그인';
  const verb = action.split('.').slice(1).join('.');
  if (/rename/.test(verb)) return '변경';
  if (/renew/.test(verb)) return '갱신';
  if (/create|add|invite/.test(verb)) return '추가';
  if (/update|change|set|assign|publish|resume|edit/.test(verb)) return '수정';
  if (/delete|remove|revoke|unassign|suspend/.test(verb)) return '삭제';
  if (/enable/.test(verb)) return '활성화';
  if (/disable/.test(verb)) return '비활성화';
  return '기타';
}

/** raw action + metadata → 내용 문구. 케이스 템플릿 우선, 폴백은 호출자 제공.
 *  metadata.acting(BO 운영자가 고객사를 대행해서 한 변경)이 있으면 "(운영사 대행: 이메일)" 꼬리표를 붙인다. */
export function auditContent(action: string, metadata: Meta, fallback: string): string {
  const c = CASES[action];
  const base = c ? c.content(metadata) : fallback;
  const acting = metadata?.['acting'] as { by?: unknown } | undefined;
  if (acting && typeof acting === 'object' && typeof acting.by === 'string' && !action.startsWith('bo.impersonation_')) {
    return `${base} (운영사 대행: ${acting.by})`;
  }
  return base;
}
